import type { PropsWithChildren } from 'react'
import React, { useEffect } from 'react'
import { secondsToString } from '@mote/helpers'
import type { AudioDataMinimal } from '@mote/helpers/media'
import { getWaveformSamples } from '@mote/helpers/media'
import type { PlayPauseButtonProps } from '../RecorderButtons/PlayPauseButton'
import { PlayPauseButton } from '../RecorderButtons/PlayPauseButton'
import { WaveformScrubber } from '../WaveformScrubber'
import { Text } from '../typography/text'
import { VisuallyHidden } from '../visuallyHidden'
import { MoteCardControlsRoot } from './MoteCardControls.styles'

export interface MoteCardControlsProps
  extends Omit<PlayPauseButtonProps, 'size'> {
  currentTime: number
  duration: number
  audioData: AudioDataMinimal
  onSeek: (time: number) => void
  onSeekEnd?: (time: number) => void
}

export const MoteCardControls = (
  props: PropsWithChildren<MoteCardControlsProps>
): JSX.Element => {
  const [waveformData, setWaveformData] = React.useState<number[]>([])
  const remainingDuration = Math.round(props.duration - props.currentTime)

  useEffect(() => {
    if (!props.audioData) return

    const endSample = Math.floor(
      (props.duration / props.audioData.durationInSeconds) *
        props.audioData.channelWaveforms[0].length
    )

    const data = getWaveformSamples(
      props.audioData.channelWaveforms[0].slice(0, endSample),
      32
    )

    setWaveformData(data)
  }, [props.audioData])

  return (
    <MoteCardControlsRoot>
      {props.children}
      <PlayPauseButton
        size='xs'
        state={props.state}
        onPlay={props.onPlay}
        onPause={props.onPause}
      />
      <WaveformScrubber
        waveformData={waveformData}
        currentTime={props.currentTime}
        duration={props.duration}
        onSeek={props.onSeek}
        onSeekEnd={props.onSeekEnd}
        size='m'
      />
      <Text as='span' fontSize='xs' lineHeight='sm' minWidth='40px'>
        <span aria-hidden={true}>{secondsToString(remainingDuration)}</span>
        <VisuallyHidden>
          {`Remaining duration: ${secondsToString(remainingDuration)}`}
        </VisuallyHidden>
      </Text>
    </MoteCardControlsRoot>
  )
}
